/**
 * Line-level diff for revision comparison in the scratchpad detail view.
 *
 * Under `MAX_LCS_CELLS` we run a classic LCS diff (single typed-array matrix,
 * so ~4 bytes per cell). Above that budget we fall back to a shared prefix
 * plus suffix diff with one removed/added block in the middle — loses per-
 * line granularity on huge bodies but never freezes the tab.
 */

export type LineDiffOp = 'equal' | 'added' | 'removed';

export type LineDiffEntry = {
    readonly op: LineDiffOp;
    readonly text: string;
};

/**
 * Cap on `beforeLines * afterLines`. ~10 MB of Int32Array cells worst case
 * (~2.5 M cells). Notes larger than this trigger the fallback so a
 * newline-heavy 20k-char body never allocates hundreds of millions of cells.
 */
export const MAX_LCS_CELLS = 2_500_000;

export function computeLineDiff(
    before: string,
    after: string,
): Array<LineDiffEntry> {
    const beforeLines = before.split('\n');
    const afterLines = after.split('\n');
    const rowCount = beforeLines.length;
    const columnCount = afterLines.length;
    if (rowCount * columnCount > MAX_LCS_CELLS) {
        return prefixSuffixFallbackDiff(beforeLines, afterLines);
    }
    return lcsDiff(beforeLines, afterLines);
}

function lcsDiff(
    beforeLines: ReadonlyArray<string>,
    afterLines: ReadonlyArray<string>,
): Array<LineDiffEntry> {
    const rowCount = beforeLines.length;
    const columnCount = afterLines.length;
    const stride = columnCount + 1;
    const table = new Int32Array((rowCount + 1) * stride);
    for (let rowIndex = rowCount - 1; rowIndex >= 0; rowIndex--) {
        for (
            let columnIndex = columnCount - 1;
            columnIndex >= 0;
            columnIndex--
        ) {
            const cellIndex = rowIndex * stride + columnIndex;
            if (beforeLines[rowIndex] === afterLines[columnIndex]) {
                table[cellIndex] =
                    (table[(rowIndex + 1) * stride + (columnIndex + 1)] ?? 0) +
                    1;
            } else {
                const below = table[(rowIndex + 1) * stride + columnIndex] ?? 0;
                const right = table[rowIndex * stride + (columnIndex + 1)] ?? 0;
                table[cellIndex] = below > right ? below : right;
            }
        }
    }
    const entries: Array<LineDiffEntry> = [];
    let rowIndex = 0;
    let columnIndex = 0;
    while (rowIndex < rowCount && columnIndex < columnCount) {
        if (beforeLines[rowIndex] === afterLines[columnIndex]) {
            entries.push({ op: 'equal', text: beforeLines[rowIndex] ?? '' });
            rowIndex++;
            columnIndex++;
        } else if (
            (table[(rowIndex + 1) * stride + columnIndex] ?? 0) >=
            (table[rowIndex * stride + (columnIndex + 1)] ?? 0)
        ) {
            entries.push({ op: 'removed', text: beforeLines[rowIndex] ?? '' });
            rowIndex++;
        } else {
            entries.push({ op: 'added', text: afterLines[columnIndex] ?? '' });
            columnIndex++;
        }
    }
    while (rowIndex < rowCount) {
        entries.push({ op: 'removed', text: beforeLines[rowIndex] ?? '' });
        rowIndex++;
    }
    while (columnIndex < columnCount) {
        entries.push({ op: 'added', text: afterLines[columnIndex] ?? '' });
        columnIndex++;
    }
    return entries;
}

function prefixSuffixFallbackDiff(
    before: ReadonlyArray<string>,
    after: ReadonlyArray<string>,
): Array<LineDiffEntry> {
    let prefix = 0;
    const maxPrefix = Math.min(before.length, after.length);
    while (prefix < maxPrefix && before[prefix] === after[prefix]) {
        prefix++;
    }
    let suffix = 0;
    while (
        suffix < before.length - prefix &&
        suffix < after.length - prefix &&
        before[before.length - 1 - suffix] === after[after.length - 1 - suffix]
    ) {
        suffix++;
    }
    const entries: Array<LineDiffEntry> = [];
    for (let index = 0; index < prefix; index++) {
        entries.push({ op: 'equal', text: before[index] ?? '' });
    }
    for (let index = prefix; index < before.length - suffix; index++) {
        entries.push({ op: 'removed', text: before[index] ?? '' });
    }
    for (let index = prefix; index < after.length - suffix; index++) {
        entries.push({ op: 'added', text: after[index] ?? '' });
    }
    for (let index = before.length - suffix; index < before.length; index++) {
        entries.push({ op: 'equal', text: before[index] ?? '' });
    }
    return entries;
}
